/**
 * useMetaCleanup Hook - COMPLETE VERSION with all cleanup methods
 */

import { useCallback, useState } from '@wordpress/element';
import { __, sprintf } from '@wordpress/i18n';
import axios from 'axios';

export const useMetaCleanup = (showToast) => {
  const [isCleaning, setIsCleaning] = useState(false);
  const [orphanedCount, setOrphanedCount] = useState(0);
  const [cleanupStats, setCleanupStats] = useState(null);

  const API_BASE = window.HavenlyticsData?.hvnlyNabApi?.url || null;
  const API_NONCE = window.HavenlyticsData?.hvnlyNabApi?.nonce || '';
  
  const AXIOS_CONFIG = {
    headers: { 
      'X-WP-Nonce': API_NONCE,
      'Content-Type': 'application/json'
    }
  };

  /**
   * Reset and cleanup combined operation
   */
  const resetAndCleanup = useCallback(async () => {
    // console.log('🧹 resetAndCleanup called');
    
    if (!API_BASE || !API_NONCE) {
      // console.error('Missing API configuration');
      return { success: false, error: 'Missing API configuration' };
    }

    setIsCleaning(true);

    try {
      const CLEANUP_ENDPOINT = `${API_BASE}/meta-cleanup/reset-and-cleanup`;
      // console.log('📡 Calling aggressive cleanup endpoint:', CLEANUP_ENDPOINT);
      
      const response = await axios.post(CLEANUP_ENDPOINT, {}, AXIOS_CONFIG);

      // console.log('✅ API Response:', response.data);

      if (response.data && response.data.success) {
        // Only show toast if showToast is provided
        if (showToast) {
          showToast(
            'success',
            __('Reset Complete', 'havenlytics'),
            response.data.deleted_count 
              ? sprintf(
                  __('Configuration reset and %d orphaned entries cleaned up', 'havenlytics'),
                  response.data.deleted_count
                )
              : __('Configuration reset successfully', 'havenlytics')
          );
        }
        
        return response.data;
      }
    } catch (error) {
      // console.error('❌ Reset and cleanup failed:', error);
      
      if (error.response) {
        // console.error('Error response:', error.response.data);
        // console.error('Error status:', error.response.status);
      }
      
      // Only show toast if showToast is provided and it's not a 404
      if (showToast && error.response?.status !== 404) {
        showToast(
          'error',
          __('Reset Failed', 'havenlytics'),
          error.response?.data?.message || error.message || __('Failed to reset and cleanup', 'havenlytics')
        );
      } else if (error.response?.status === 404) {
        // console.log('ℹ️ Cleanup endpoint not available (404) - continuing with regular reset');
      }
      
      return { success: false, error: error.message };
    } finally {
      setIsCleaning(false);
    }
  }, [API_BASE, API_NONCE, showToast]);

  /**
   * Cleanup ALL orphaned meta across all properties
   */
  const cleanupAllOrphaned = useCallback(async () => {
    // console.log('🧹 cleanupAllOrphaned called');
    
    if (!API_BASE || !API_NONCE) {
      // console.error('Missing API configuration');
      return { success: false, error: 'Missing API configuration' };
    }

    setIsCleaning(true);

    try {
      const CLEANUP_ENDPOINT = `${API_BASE}/meta-cleanup/cleanup-all`;
      const response = await axios.post(CLEANUP_ENDPOINT, {}, AXIOS_CONFIG);

      if (response.data && response.data.success) {
        if (showToast) {
          showToast(
            'success',
            __('Database Cleanup Complete', 'havenlytics'),
            sprintf(
              __('Removed %d orphaned entries from %d properties', 'havenlytics'),
              response.data.deleted_count,
              response.data.properties_processed
            )
          );
        }
        
        return response.data;
      }
    } catch (error) {
      // console.error('❌ Cleanup all failed:', error);
      if (showToast) {
        showToast(
          'error',
          __('Cleanup Failed', 'havenlytics'),
          error.response?.data?.message || error.message || __('Failed to clean up database', 'havenlytics')
        );
      }
    } finally {
      setIsCleaning(false);
    }
  }, [API_BASE, API_NONCE, showToast]);

  /**
   * FINAL cleanup - removes old legacy data while keeping active fields
   */
  const finalCleanup = useCallback(async () => {
    // console.log('🧹 finalCleanup called');
    
    if (!API_BASE || !API_NONCE) {
      // console.error('Missing API configuration');
      return { success: false, error: 'Missing API configuration' };
    }

    setIsCleaning(true);

    try {
      const CLEANUP_ENDPOINT = `${API_BASE}/meta-cleanup/final-cleanup`;
      const response = await axios.post(CLEANUP_ENDPOINT, {}, AXIOS_CONFIG);

      if (response.data && response.data.success) {
        if (showToast) {
          showToast(
            'success',
            __('Final Cleanup Complete', 'havenlytics'),
            sprintf(
              __('Removed %d old entries, kept %d active keys', 'havenlytics'),
              response.data.deleted_count,
              response.data.kept_keys_count
            )
          );
        }
        
        return response.data;
      }
    } catch (error) {
      // console.error('❌ Final cleanup failed:', error);
      if (showToast) {
        showToast(
          'error',
          __('Cleanup Failed', 'havenlytics'),
          error.response?.data?.message || error.message || __('Failed to clean up database', 'havenlytics')
        );
      }
    } finally {
      setIsCleaning(false);
    }
  }, [API_BASE, API_NONCE, showToast]);

  /**
   * Get orphaned meta stats
   */
  const getOrphanedStats = useCallback(async (propertyId = 0) => {
    try {
      const endpoint = propertyId 
        ? `${API_BASE}/meta-cleanup/orphaned?property_id=${propertyId}`
        : `${API_BASE}/meta-cleanup/orphaned`;
      
      const response = await axios.get(endpoint, AXIOS_CONFIG);
      
      if (response.data && response.data.success) {
        setOrphanedCount(response.data.total);
        return response.data.data;
      }
    } catch (error) {
      // console.error('Failed to get orphaned stats:', error);
    }
  }, [API_BASE, AXIOS_CONFIG]);

  /**
   * Get cleanup statistics
   */
  const getCleanupStats = useCallback(async () => {
    try {
      const endpoint = `${API_BASE}/meta-cleanup/stats`;
      const response = await axios.get(endpoint, AXIOS_CONFIG);
      
      if (response.data && response.data.success) {
        setCleanupStats(response.data.data);
        return response.data.data;
      }
    } catch (error) {
      // console.error('Failed to get cleanup stats:', error);
    }
  }, [API_BASE, AXIOS_CONFIG]);

  /**
   * Cleanup orphaned meta for specific fields
   */
  const cleanupOrphanedMeta = useCallback(async (fields, propertyId = 0) => {
    if (!fields || fields.length === 0) {
      return { success: true, deleted_count: 0 };
    }

    if (!API_BASE || !API_NONCE) {
      // console.error('Missing API configuration');
      return { success: false, error: 'Missing API configuration' };
    }

    setIsCleaning(true);

    try {
      const endpoint = `${API_BASE}/meta-cleanup/cleanup`;
      const response = await axios.post(endpoint, {
        fields: fields,
        property_id: propertyId
      }, AXIOS_CONFIG);

      if (response.data && response.data.success) {
        if (showToast) {
          showToast(
            'success',
            __('Cleanup Complete', 'havenlytics'),
            sprintf(
              __('Removed %d orphaned meta entries', 'havenlytics'),
              response.data.deleted_count
            )
          );
        }
        
        // Refresh stats
        await getOrphanedStats(propertyId);
        
        return response.data;
      }
    } catch (error) {
      // console.error('Cleanup failed:', error);
      if (showToast) {
        showToast(
          'error',
          __('Cleanup Failed', 'havenlytics'),
          error.response?.data?.message || error.message || __('Failed to clean up orphaned meta', 'havenlytics')
        );
      }
    } finally {
      setIsCleaning(false);
    }
  }, [API_BASE, AXIOS_CONFIG, showToast, getOrphanedStats]);

  /**
   * Extract field names from tabs
   */
  const extractFieldNames = useCallback((tabs) => {
    const fieldNames = [];
    
    tabs.forEach(tab => {
      if (tab.fields && Array.isArray(tab.fields)) {
        tab.fields.forEach(field => {
          if (field.name) fieldNames.push(field.name);
          else if (field.id) fieldNames.push(field.id);
          if (field.migrated_from) fieldNames.push(field.migrated_from);
        });
      }
    });
    
    return [...new Set(fieldNames)];
  }, []);

  return {
    isCleaning,
    orphanedCount,
    cleanupStats,
    extractFieldNames,
    getOrphanedStats,
    getCleanupStats,
    cleanupOrphanedMeta,
    resetAndCleanup,
    cleanupAllOrphaned,
    finalCleanup
  };
};